import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifyError } from "../../../utils/notify";
import { IconSearch } from "@tabler/icons-react";
import SettingsTabs from "../../settings/common/SettingsTabs";
import { useClient } from "../../../context/ClientContext";
import { useClientGroups } from "../../../hooks/useClientGroups";
import {
  ClientGroup,
  ClientGroupType,
  RuleTree,
} from "../../../types/clientGroups";
import SentenceRuleBuilder from "./SentenceRuleBuilder";
import { SectionLabel } from "./sectionLabel";
import { emptyRuleTree } from "./ruleModel";
import { fieldStyles } from "./fieldStyles";

interface PickableClient {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
}

interface Props {
  initialType: ClientGroupType;
  onCancel: () => void;
  onCreated: (group: ClientGroup) => void;
}

/**
 * Create-group editor as a centered, fit-to-content modal (per feedback — the
 * former full-page overlay read as bad design). The type is a tab
 * (Dynamic / Static) rather than a switch link. One screen, no steps.
 */
export default function ClientGroupEditor({
  initialType,
  onCancel,
  onCreated,
}: Props) {
  const { createGroup } = useClientGroups();
  const { allClients } = useClient() as { allClients: PickableClient[] };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<ClientGroupType>(initialType);
  const [rules, setRules] = useState<RuleTree>(emptyRuleTree());
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const isDynamic = type === "dynamic";
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (allClients || [])
      .filter((c) => {
        if (!q) return true;
        const n = `${c.first_name ?? ""} ${c.last_name ?? ""}`.toLowerCase();
        return n.includes(q) || (c.email ?? "").toLowerCase().includes(q);
      })
      .slice(0, 200);
  }, [allClients, search]);

  const canCreate = name.trim().length > 0 && (isDynamic || picked.size > 0);

  const create = async () => {
    setSaving(true);
    try {
      const group = await createGroup({
        name: name.trim(),
        description: description.trim() || null,
        group_type: type,
        ...(isDynamic ? { rules } : { member_client_ids: Array.from(picked) }),
      });
      onCreated(group);
    } catch (e: any) {
      notifyError(e?.message || "Could not create group");
    } finally {
      setSaving(false);
    }
  };

  const cname = (c: { first_name?: string; last_name?: string }) =>
    `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—";

  return (
    <Modal
      opened
      onClose={onCancel}
      yOffset="8dvh"
      size={760}
      radius={12}
      title={
        <Text fw={600} size="18px">
          New client group
        </Text>
      }
      styles={{ body: { paddingTop: 8 } }}
    >
      <Stack gap={20}>
        <SettingsTabs
          ariaLabel="Group type"
          value={type}
          onChange={(v) => setType(v as ClientGroupType)}
          items={[
            { value: "dynamic", label: "Dynamic" },
            { value: "static", label: "Static" },
          ]}
        />

        <Box>
          <Text size="11.5px" fw={600} c="dimmed" mb={6}>
            Name
          </Text>
          <TextInput
            styles={fieldStyles}
            maw={420}
            placeholder="Untitled group"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </Box>

        <Box>
          <Text size="11.5px" fw={600} c="dimmed" mb={6}>
            Description
          </Text>
          <TextInput
            styles={fieldStyles}
            maw={420}
            placeholder="What this group is for"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
        </Box>

        <Box
          style={{
            borderTop: "1px solid #e6e6e6",
            paddingTop: 22,
            maxHeight: "52dvh",
            overflow: "auto",
          }}
        >
          {isDynamic ? (
            <>
              <SectionLabel label="Definition" />
              <Box mt={12}>
                <SentenceRuleBuilder
                  value={rules}
                  onChange={setRules}
                  countPlacement="top"
                />
              </Box>
            </>
          ) : (
            <>
              <Group justify="space-between" align="center" mb={8}>
                <SectionLabel label="Members" />
                <Text size="12.5px" c="dimmed">
                  {picked.size} selected
                </Text>
              </Group>
              <TextInput
                styles={fieldStyles}
                maw={320}
                mb={8}
                placeholder="Search clients"
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
              <Box
                style={{
                  border: "1px solid #e6e6e6",
                  borderRadius: 12,
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {candidates.map((c) => {
                  const on = picked.has(c.id);
                  const toggle = () => {
                    const next = new Set(picked);
                    on ? next.delete(c.id) : next.add(c.id);
                    setPicked(next);
                  };
                  return (
                    <Group
                      key={c.id}
                      wrap="nowrap"
                      h={44}
                      px={14}
                      gap={12}
                      style={{
                        borderBottom: "1px solid #ededed",
                        cursor: "pointer",
                      }}
                      onClick={toggle}
                    >
                      <Checkbox checked={on} readOnly tabIndex={-1} />
                      <Text
                        size="13px"
                        fw={500}
                        style={{ flex: 1 }}
                        lineClamp={1}
                      >
                        {cname(c)}
                      </Text>
                      <Text size="13px" c="dimmed" lineClamp={1} maw={240}>
                        {c.email}
                      </Text>
                    </Group>
                  );
                })}
              </Box>
            </>
          )}
        </Box>

        <Group justify="flex-end" gap="sm" pt={4}>
          <Button variant="subtle" color="gray" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            disabled={!canCreate}
            loading={saving}
            onClick={create}
          >
            Create Group
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
